// Managed hosts (N) — list + modal create/edit form. Mirrors SwitchesSection +
// the grouped-form pattern of NetbirdEditor. slug is immutable (create-only).
// Render-critical: slug + role + address (address = the host's management IP at
// render). Secret refs use the strict #field vault guard; address/gateway use
// the lenient IP guard.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteHosts } from "../../api/endpoints/siteHosts";
import { qk } from "../../api/queryKeys";
import {
  ANSIBLE_CONNECTIONS,
  HOST_ROLES,
  type AnsibleConnection,
  type HostConfig,
  type HostCreateIn,
  type HostFields,
  type HostRole,
} from "../../api/types/siteHost";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { NumberField } from "../../components/forms/NumberField";
import { SelectField } from "../../components/forms/SelectField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { StringListField } from "../../components/forms/StringListField";
import { Table, type Column } from "../../components/ui/Table";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { parseVendorExtras, vaultFieldRefError, ipAddressError } from "../../lib/fieldValidators";

export function HostsSection({ siteSlug, canWrite }: { siteSlug: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<HostConfig | null>(null);
  const [adding, setAdding] = useState(false);

  const q = useQuery({
    queryKey: qk.siteHosts(siteSlug),
    queryFn: () => siteHosts.list(siteSlug).then((r) => r.data),
  });
  const del = useMutation({
    mutationFn: (hostSlug: string) => siteHosts.remove(siteSlug, hostSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.siteHosts(siteSlug) }),
  });

  const columns: ReadonlyArray<Column<HostConfig>> = [
    { key: "slug", header: "Slug", render: (h) => <code>{h.slug}</code> },
    { key: "display_name", header: "Name", render: (h) => h.display_name ?? "—" },
    { key: "role", header: "Role", render: (h) => h.role ?? "—" },
    // Address is the render's management IP (request_doc uses h.address only),
    // so show it on its own — never fall back to ansible_host, or a host with
    // an SSH target but no address would look render-ready when it isn't.
    { key: "address", header: "Address", render: (h) => h.address ?? "—" },
    { key: "ansible_host", header: "SSH target", render: (h) => h.ansible_host ?? "—" },
    { key: "fqdn", header: "FQDN", render: (h) => h.fqdn ?? "—" },
    {
      key: "actions", header: "", align: "right",
      render: (h) => canWrite ? (
        <span style={{ display: "inline-flex", gap: 8 }}>
          <button type="button" style={linkBtn} onClick={() => setEditing(h)}>Edit</button>
          <button type="button" style={dangerLinkBtn} disabled={del.isPending}
            onClick={() => { if (window.confirm(`Delete host ${h.slug}?`)) del.mutate(h.slug); }}>Delete</button>
        </span>
      ) : null,
    },
  ];

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading hosts…</p>;
  if (q.error) return <ErrorBanner error={q.error} title="Failed to load hosts" />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Managed hosts</h3>
        {canWrite && <button type="button" style={primaryBtn} onClick={() => setAdding(true)}>Add host</button>}
      </div>
      {del.error && <ErrorBanner error={del.error} title="Delete failed" />}
      <Table<HostConfig>
        columns={columns}
        rows={[...(q.data ?? [])].sort((a, b) => a.slug.localeCompare(b.slug))}
        rowKey={(h) => h.id}
        empty={<EmptyState title="No managed hosts" description="The edge/compute boxes for this site (≥1 needed to render)." />}
      />
      <Modal open={adding || editing !== null} onClose={() => { setAdding(false); setEditing(null); }}
        title={editing ? `Edit ${editing.slug}` : "Add host"} width={640}>
        <HostForm siteSlug={siteSlug} canWrite={canWrite} host={editing ?? undefined}
          onDone={() => { setAdding(false); setEditing(null); }} />
      </Modal>
    </div>
  );
}

interface HostDraft {
  slug: string; display_name: string; role: HostRole | ""; fqdn: string;
  ansible_host: string; ansible_user: string; ansible_port: number | null; ansible_connection: AnsibleConnection;
  proxy_jump_host: string; connection_secret_ref: string; become_secret_ref: string; break_glass_secret_ref: string;
  address: string; gateway: string; dns_servers: string[]; vlan_name: string; mgmt_vlan_enabled: boolean; switch_port: number | null;
  site_profile: string; switch_profile: string; controller_profile: string;
  homeassistant_enabled: boolean; frigate_enabled: boolean; mosquitto_enabled: boolean; go2rtc_enabled: boolean; solar_enabled: boolean;
  custom_services: string[]; notes: string[]; vendor_extras_json: string;
}

const ROLE_OPTIONS = [{ value: "", label: "— select role —" }, ...HOST_ROLES];
const HOST_SERVICE_FLAGS: ReadonlyArray<[keyof HostDraft, string]> = [
  ["homeassistant_enabled", "Home Assistant"], ["frigate_enabled", "Frigate"], ["mosquitto_enabled", "Mosquitto"],
  ["go2rtc_enabled", "go2rtc"], ["solar_enabled", "Solar"],
];

function HostForm({
  siteSlug, canWrite, host, onDone,
}: {
  siteSlug: string; canWrite: boolean; host?: HostConfig; onDone: () => void;
}) {
  const qc = useQueryClient();
  const editing = host !== undefined;
  const [draft, setDraft] = useState<HostDraft>(() => toDraft(host));
  const set = <K extends keyof HostDraft>(k: K, v: HostDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const vendor = parseVendorExtras(draft.vendor_extras_json);
  const vendorExtras = vendor.ok ? vendor.value : {};
  const secretErrs = {
    connection_secret_ref: vaultFieldRefError(draft.connection_secret_ref),
    become_secret_ref: vaultFieldRefError(draft.become_secret_ref),
    break_glass_secret_ref: vaultFieldRefError(draft.break_glass_secret_ref),
  };
  const addrErr = ipAddressError(draft.address);
  const gwErr = ipAddressError(draft.gateway);

  const mutation = useMutation({
    mutationFn: () =>
      editing
        ? siteHosts.update(siteSlug, host!.slug, fromDraft(draft, vendorExtras, false))
        : siteHosts.create(siteSlug, fromDraft(draft, vendorExtras, true) as HostCreateIn),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.siteHosts(siteSlug) }); onDone(); },
  });

  const isValid =
    (editing || draft.slug.trim() !== "") &&
    draft.role !== "" &&
    draft.address.trim() !== "" && addrErr === null && gwErr === null &&
    Object.values(secretErrs).every((e) => e === null) &&
    vendor.ok;
  const disabled = !canWrite || mutation.isPending;
  const refBase = `vault:tenants/<slug>/hosts/${draft.slug || "<host>"}`;

  return (
    <FormShell
      submitLabel={editing ? "Save host" : "Create host"}
      onSubmit={(e) => { e.preventDefault(); if (!isValid || disabled) return; mutation.mutate(); }}
      onCancel={onDone}
      pending={mutation.isPending}
      submitDisabled={!canWrite || !isValid}
      error={mutation.error}
      errorTitle={editing ? "Save host failed" : "Add host failed"}
    >
      {/* Identity */}
      {!editing && (
        <TextField label="Slug" value={draft.slug} onChange={(v) => set("slug", v)} required
          placeholder="svc-fatima-01" helper="Immutable after create." disabled={disabled} />
      )}
      <SelectField label="Role" value={draft.role} onChange={(v) => set("role", v as HostRole | "")}
        required options={ROLE_OPTIONS} disabled={disabled} />
      <TextField label="Display name" value={draft.display_name} onChange={(v) => set("display_name", v)} disabled={disabled} />

      {/* Connection */}
      <details open>
        <summary style={summary}>Connection</summary>
        <div style={section}>
          <TextField label="FQDN" value={draft.fqdn} onChange={(v) => set("fqdn", v)} disabled={disabled} />
          <TextField label="Ansible host" value={draft.ansible_host} onChange={(v) => set("ansible_host", v)}
            placeholder="10.129.1.42" helper="SSH target (optional; address is the render IP)." disabled={disabled} />
          <TextField label="Ansible user" value={draft.ansible_user} onChange={(v) => set("ansible_user", v)} disabled={disabled} />
          <NumberField label="Ansible port" value={draft.ansible_port} onChange={(v) => set("ansible_port", v)} min={1} max={65535} placeholder="22" disabled={disabled} />
          <SelectField label="Ansible connection" value={draft.ansible_connection}
            onChange={(v) => set("ansible_connection", v as AnsibleConnection)} options={ANSIBLE_CONNECTIONS} disabled={disabled} />
          <TextField label="Proxy jump host" value={draft.proxy_jump_host} onChange={(v) => set("proxy_jump_host", v)}
            helper="A sibling host slug (validated at render)." disabled={disabled} />
          <TextField label="Connection secret ref" value={draft.connection_secret_ref} onChange={(v) => set("connection_secret_ref", v)}
            placeholder={`${refBase}/connection#password`} helper="Field-qualified vault: ref." error={secretErrs.connection_secret_ref} disabled={disabled} />
          <TextField label="Become secret ref" value={draft.become_secret_ref} onChange={(v) => set("become_secret_ref", v)}
            placeholder={`${refBase}/become#password`} error={secretErrs.become_secret_ref} disabled={disabled} />
          <TextField label="Break-glass secret ref" value={draft.break_glass_secret_ref} onChange={(v) => set("break_glass_secret_ref", v)}
            placeholder={`${refBase}/break_glass#password`} error={secretErrs.break_glass_secret_ref} disabled={disabled} />
        </div>
      </details>

      {/* Network placement */}
      <details open>
        <summary style={summary}>Network placement</summary>
        <div style={section}>
          <TextField label="Address" value={draft.address} onChange={(v) => set("address", v)} required
            placeholder="10.129.1.42/24" helper="Management IP/CIDR — render-critical." error={addrErr} disabled={disabled} />
          <TextField label="Gateway" value={draft.gateway} onChange={(v) => set("gateway", v)} placeholder="10.129.1.1" error={gwErr} disabled={disabled} />
          <StringListField label="DNS servers" value={draft.dns_servers} onChange={(v) => set("dns_servers", v)} helper="One per line." disabled={disabled} />
          <TextField label="VLAN name" value={draft.vlan_name} onChange={(v) => set("vlan_name", v)}
            helper="A generated VLAN name (validated at render)." disabled={disabled} />
          <CheckboxField label="Mgmt VLAN enabled" checked={draft.mgmt_vlan_enabled} onChange={(v) => set("mgmt_vlan_enabled", v)} disabled={disabled} />
          <NumberField label="Switch port" value={draft.switch_port} onChange={(v) => set("switch_port", v)} min={1} placeholder="1" disabled={disabled} />
        </div>
      </details>

      {/* Profiles */}
      <details>
        <summary style={summary}>Provisioning profiles</summary>
        <div style={section}>
          <TextField label="Site profile" value={draft.site_profile} onChange={(v) => set("site_profile", v)} disabled={disabled} />
          <TextField label="Switch profile" value={draft.switch_profile} onChange={(v) => set("switch_profile", v)} disabled={disabled} />
          <TextField label="Controller profile" value={draft.controller_profile} onChange={(v) => set("controller_profile", v)} disabled={disabled} />
        </div>
      </details>

      {/* Services */}
      <details>
        <summary style={summary}>Per-host services</summary>
        <div style={section}>
          {HOST_SERVICE_FLAGS.map(([k, label]) => (
            <CheckboxField key={k} label={label} checked={Boolean(draft[k])} onChange={(v) => set(k, v as never)} disabled={disabled} />
          ))}
          <StringListField label="Custom services" value={draft.custom_services} onChange={(v) => set("custom_services", v)} helper="One per line." disabled={disabled} />
        </div>
      </details>

      {/* Meta */}
      <StringListField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} helper="One per line." disabled={disabled} />
      <details>
        <summary style={summary}>Vendor extras (advanced, JSON)</summary>
        <textarea value={draft.vendor_extras_json} disabled={disabled} rows={5}
          onChange={(e) => set("vendor_extras_json", e.target.value)} placeholder={"{}"}
          style={{ width: "100%", marginTop: 8, padding: "8px 10px", background: "var(--surface-2)",
            border: `1px solid ${vendor.ok ? "var(--border)" : "var(--crit)"}`, borderRadius: 4,
            color: "var(--text)", fontFamily: "monospace", fontSize: "0.85rem", resize: "vertical" }} />
        {!vendor.ok && <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{vendor.error}</span>}
      </details>
    </FormShell>
  );
}

function toDraft(h?: HostConfig): HostDraft {
  const hasExtras = Object.keys(h?.vendor_extras ?? {}).length > 0;
  return {
    slug: h?.slug ?? "", display_name: h?.display_name ?? "", role: h?.role ?? "", fqdn: h?.fqdn ?? "",
    ansible_host: h?.ansible_host ?? "", ansible_user: h?.ansible_user ?? "",
    ansible_port: h?.ansible_port ?? null,
    ansible_connection: h?.ansible_connection ?? "ssh", proxy_jump_host: h?.proxy_jump_host ?? "",
    connection_secret_ref: h?.connection_secret_ref ?? "", become_secret_ref: h?.become_secret_ref ?? "",
    break_glass_secret_ref: h?.break_glass_secret_ref ?? "", address: h?.address ?? "", gateway: h?.gateway ?? "",
    dns_servers: h?.dns_servers ?? [], vlan_name: h?.vlan_name ?? "", mgmt_vlan_enabled: Boolean(h?.mgmt_vlan_enabled),
    switch_port: h?.switch_port ?? null,
    site_profile: h?.site_profile ?? "", switch_profile: h?.switch_profile ?? "", controller_profile: h?.controller_profile ?? "",
    homeassistant_enabled: Boolean(h?.homeassistant_enabled), frigate_enabled: Boolean(h?.frigate_enabled),
    mosquitto_enabled: Boolean(h?.mosquitto_enabled), go2rtc_enabled: Boolean(h?.go2rtc_enabled), solar_enabled: Boolean(h?.solar_enabled),
    custom_services: h?.custom_services ?? [], notes: h?.notes ?? [],
    vendor_extras_json: hasExtras ? JSON.stringify(h?.vendor_extras, null, 2) : "",
  };
}

function fromDraft(d: HostDraft, vendorExtras: Record<string, unknown>, withSlug: boolean): HostFields & { slug?: string } {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  const fields: HostFields = {
    display_name: orNull(d.display_name), role: d.role === "" ? null : d.role, fqdn: orNull(d.fqdn),
    ansible_host: orNull(d.ansible_host), ansible_user: orNull(d.ansible_user), ansible_port: d.ansible_port,
    ansible_connection: d.ansible_connection, proxy_jump_host: orNull(d.proxy_jump_host),
    connection_secret_ref: orNull(d.connection_secret_ref), become_secret_ref: orNull(d.become_secret_ref),
    break_glass_secret_ref: orNull(d.break_glass_secret_ref), address: orNull(d.address), gateway: orNull(d.gateway),
    dns_servers: d.dns_servers, vlan_name: orNull(d.vlan_name), mgmt_vlan_enabled: d.mgmt_vlan_enabled,
    switch_port: d.switch_port, site_profile: orNull(d.site_profile), switch_profile: orNull(d.switch_profile),
    controller_profile: orNull(d.controller_profile), homeassistant_enabled: d.homeassistant_enabled,
    frigate_enabled: d.frigate_enabled, mosquitto_enabled: d.mosquitto_enabled, go2rtc_enabled: d.go2rtc_enabled,
    solar_enabled: d.solar_enabled, custom_services: d.custom_services, notes: d.notes, vendor_extras: vendorExtras,
  };
  return withSlug ? { slug: d.slug.trim(), ...fields } : fields;
}

const summary: React.CSSProperties = { cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" };
const section: React.CSSProperties = { display: "grid", gap: 14, marginTop: 12 };
const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "6px 12px",
  borderRadius: 4, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: "0.85rem", padding: 0,
};
const dangerLinkBtn: React.CSSProperties = { ...linkBtn, color: "var(--crit)" };
